import { useEffect, useRef, useState } from 'react';

export type ToastPosition = 'bottom-right' | 'top-center';

// `hidden` parks the toast just off-screen on the side it is anchored to, so
// it slides in and out from that edge.
const POSITION_CLASSES: Record<ToastPosition, { anchor: string; shown: string; hidden: string }> = {
  'bottom-right': { anchor: 'right-24 mr-1', shown: 'bottom-4', hidden: '-bottom-24' },
  // ATAD-136: clear of the DocsBot widget, which owns the bottom-right corner
  // on the Audience Builder.
  'top-center': { anchor: 'left-1/2 -translate-x-1/2', shown: 'top-4', hidden: '-top-24' },
};

interface ToastNotificationProps {
  message: string;
  success?: boolean | null | undefined;
  error?: boolean | null | undefined;
  show?: boolean | null | undefined;
  /**
   * Milliseconds to stay up before calling `onDismiss`. Omit to leave
   * dismissal entirely to the caller — which is what every existing caller
   * does, so their behaviour is unchanged.
   */
  duration?: number;
  onDismiss?: () => void;
  /**
   * Where the toast sits. Defaults to the bottom-right corner, which is what
   * every caller relied on before this prop existed.
   */
  position?: ToastPosition;
}

export default function ToastNotification({
  message,
  success,
  error,
  show,
  duration,
  onDismiss,
  position = 'bottom-right',
}: ToastNotificationProps) {
  const placement = POSITION_CLASSES[position];
  const [hovered, setHovered] = useState(false);
  const remainingRef = useRef(duration ?? 0);
  const onDismissRef = useRef(onDismiss);

  // Held in a ref so an inline arrow from the caller doesn't restart the
  // countdown on every render.
  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  // A new message, or the toast being raised again, is a fresh countdown.
  useEffect(() => {
    remainingRef.current = duration ?? 0;
  }, [duration, message, show]);

  useEffect(() => {
    if (!show || !duration || hovered) {
      return undefined;
    }

    const startedAt = Date.now();
    const timer = setTimeout(() => onDismissRef.current?.(), remainingRef.current);

    return () => {
      clearTimeout(timer);
      // Pointing at the toast pauses it rather than restarting it: bank
      // whatever time was left so hovering can't extend a toast forever, and
      // reading a long message can't be cut short either.
      remainingRef.current = Math.max(
        0,
        remainingRef.current - (Date.now() - startedAt)
      );
    };
  }, [show, duration, hovered, message]);

  return (
    <div
      className={`fixed z-50 border-l-8 ${placement.anchor} py-3 px-6 rounded-lg bg-white shadow-lg w-60 max-w-[calc(100vw-8rem)] transition-all duration-300 ease-in-out ${
        show ? placement.shown : placement.hidden
      } ${success ? 'border-primary' : ''} ${error ? 'border-secondary' : ''}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      role="status"
      aria-live={error ? 'assertive' : 'polite'}
      title={message}
    >
      <p className="line-clamp-3 break-words">{message}</p>
    </div>
  );
}
